import moment from 'moment';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { accessTypes } from '../../helpers/Utils';
import { Colxx } from '../common/CustomBootstrap';
import { prepareChartData } from './DailyAnalyticsFetcher';
import InteractiveBarChart from './InteractiveBarChart';

const DashboardCharts = ({ dateRange, dailyAnalytics, previousDailyAnalytics, loading, error, filterBySpId = null }) => {
    // Get user from Redux store
    const { user } = useSelector(state => state.authUser);

    // Determine which SP to use for filtering
    // Priority: filterBySpId prop (from SelectedServiceProvider) > user's SP ID > null (show all)
    let spId = filterBySpId;
    if (!spId) {
        const role = user?.accessType || 'superAdmin';
        spId = !accessTypes.includes(role) ? user?.uid : null;
    }

    // Memoize date range label
    const dateRangeLabel = useMemo(() => {
        const daysCount = dateRange?.endDate && dateRange?.startDate
            ? moment(dateRange.endDate).diff(moment(dateRange.startDate), 'days') + 1
            : 7;
        return dateRange?.startDate && dateRange?.endDate
            ? `${moment(dateRange.startDate).format('MMM DD, YYYY')} - ${moment(dateRange.endDate).format('MMM DD, YYYY')}`
            : `Last ${daysCount} days`;
    }, [dateRange?.startDate, dateRange?.endDate]);

    // Prepare online and offline bookings data together (for stacked chart)
    const onlineOfflineBookingsData = useMemo(() => {
        if (!dailyAnalytics || dailyAnalytics.length === 0) return [];

        return dailyAnalytics.map(day => {
            let online = 0;
            let offline = 0;

            if (spId) {
                // SP level data
                if (day.SP && day.SP[spId]) {
                    online = day.SP[spId]?.counters?.onlineBooking || 0;
                    offline = day.SP[spId]?.counters?.offlineBooking || 0;
                }
            } else {
                // General level data
                online = day.general?.counters?.onlineBooking || 0;
                offline = day.general?.counters?.offlineBooking || 0;
            }

            return {
                date: day.date,
                online: online,
                offline: offline,
                timestamp: day.timestamp,
            };
        });
    }, [dailyAnalytics, spId]);

    const revenueData = useMemo(() =>
        dailyAnalytics && dailyAnalytics.length > 0
            ? prepareChartData(dailyAnalytics, 'totalRevenu', spId ? 'SP' : 'general')
            : []
        , [dailyAnalytics, spId]);

    const usersData = useMemo(() =>
        dailyAnalytics && dailyAnalytics.length > 0
            ? prepareChartData(dailyAnalytics, 'totalUsers', spId ? 'SP' : 'general')
            : []
        , [dailyAnalytics, spId]);

    // Prepare canceled and rejected data together
    const canceledRejectedData = useMemo(() => {
        if (!dailyAnalytics || dailyAnalytics.length === 0) return [];

        return dailyAnalytics.map(day => {
            let canceled = 0;
            let rejected = 0;

            if (spId) {
                // SP level data
                if (day.SP && day.SP[spId]) {
                    canceled = day.SP[spId]?.counters?.canceled || 0;
                    rejected = day.SP[spId]?.counters?.rejected || 0;
                }
            } else {
                // General level data
                canceled = day.general?.counters?.canceled || 0;
                rejected = day.general?.counters?.rejected || 0;
            }

            return {
                date: day.date,
                canceled: canceled,
                rejected: rejected,
                timestamp: day.timestamp,
            };
        });
    }, [dailyAnalytics, spId]);

    // Memoize date ranges for current and previous periods
    const { currentPeriodRange, previousPeriodRange } = useMemo(() => {
        if (!dateRange?.startDate || !dateRange?.endDate) {
            return { currentPeriodRange: null, previousPeriodRange: null };
        }

        const currentStart = moment(dateRange.startDate);
        const currentEnd = moment(dateRange.endDate);
        const periodLength = currentEnd.diff(currentStart, 'days');

        const previousStart = currentStart.clone().subtract(periodLength + 1, 'days');
        const previousEnd = currentStart.clone().subtract(1, 'days');

        return {
            currentPeriodRange: `${currentStart.format('MMM DD, YYYY')} - ${currentEnd.format('MMM DD, YYYY')}`,
            previousPeriodRange: `${previousStart.format('MMM DD, YYYY')} - ${previousEnd.format('MMM DD, YYYY')}`
        };
    }, [dateRange?.startDate, dateRange?.endDate]);

    // Memoize trend calculations
    const { bookingsTrend, revenueTrend, usersTrend, canceledRejectedTrend } = useMemo(() => {
        const calculateTrend = (metric, level = 'general') => {
            if (!dailyAnalytics || dailyAnalytics.length === 0 || !previousDailyAnalytics || previousDailyAnalytics.length === 0) {
                return null;
            }

            let currentTotal = 0;
            let previousTotal = 0;

            // Calculate current period total
            dailyAnalytics.forEach(day => {
                if (level === 'general') {
                    // Handle metrics from counters (like canceled, rejected, etc.)
                    if (metric === 'canceled' || metric === 'rejected' || metric === 'onlineBooking' || metric === 'offlineBooking') {
                        currentTotal += day.general?.counters?.[metric] || 0;
                    } else {
                        currentTotal += day.general?.[metric] || 0;
                    }
                } else if (level === 'SP' && spId) {
                    if (day.SP && day.SP[spId]) {
                        // Handle metrics from counters for SP level
                        if (metric === 'canceled' || metric === 'rejected' || metric === 'onlineBooking' || metric === 'offlineBooking') {
                            currentTotal += day.SP[spId]?.counters?.[metric] || 0;
                        } else {
                            currentTotal += day.SP[spId][metric] || 0;
                        }
                    }
                }
            });

            // Calculate previous period total
            previousDailyAnalytics.forEach(day => {
                if (level === 'general') {
                    // Handle metrics from counters (like canceled, rejected, etc.)
                    if (metric === 'canceled' || metric === 'rejected' || metric === 'onlineBooking' || metric === 'offlineBooking') {
                        previousTotal += day.general?.counters?.[metric] || 0;
                    } else {
                        previousTotal += day.general?.[metric] || 0;
                    }
                } else if (level === 'SP' && spId) {
                    if (day.SP && day.SP[spId]) {
                        // Handle metrics from counters for SP level
                        if (metric === 'canceled' || metric === 'rejected' || metric === 'onlineBooking' || metric === 'offlineBooking') {
                            previousTotal += day.SP[spId]?.counters?.[metric] || 0;
                        } else {
                            previousTotal += day.SP[spId][metric] || 0;
                        }
                    }
                }
            });

            const trendPercentage = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : (currentTotal > 0 ? 100 : null);

            // Debug logging
            if (process.env.NODE_ENV === 'development') {
                console.log(`Trend for ${metric} (${level}):`, {
                    currentTotal,
                    previousTotal,
                    trendPercentage,
                    currentPeriodDays: dailyAnalytics.length,
                    previousPeriodDays: previousDailyAnalytics.length
                });
            }

            return {
                percentage: trendPercentage,
                currentTotal,
                previousTotal,
                currentPeriod: currentPeriodRange,
                previousPeriod: previousPeriodRange
            };
        };

        // Calculate combined trend for online + offline bookings
        const calculateOnlineOfflineTrend = () => {
            if (!dailyAnalytics || dailyAnalytics.length === 0 || !previousDailyAnalytics || previousDailyAnalytics.length === 0) {
                return null;
            }

            let currentOnline = 0;
            let currentOffline = 0;
            let previousOnline = 0;
            let previousOffline = 0;

            // Calculate current period totals
            dailyAnalytics.forEach(day => {
                if (spId) {
                    // SP level
                    if (day.SP && day.SP[spId]) {
                        currentOnline += day.SP[spId]?.counters?.onlineBooking || 0;
                        currentOffline += day.SP[spId]?.counters?.offlineBooking || 0;
                    }
                } else {
                    // General level
                    currentOnline += day.general?.counters?.onlineBooking || 0;
                    currentOffline += day.general?.counters?.offlineBooking || 0;
                }
            });

            // Calculate previous period totals
            previousDailyAnalytics.forEach(day => {
                if (spId) {
                    // SP level
                    if (day.SP && day.SP[spId]) {
                        previousOnline += day.SP[spId]?.counters?.onlineBooking || 0;
                        previousOffline += day.SP[spId]?.counters?.offlineBooking || 0;
                    }
                } else {
                    // General level
                    previousOnline += day.general?.counters?.onlineBooking || 0;
                    previousOffline += day.general?.counters?.offlineBooking || 0;
                }
            });

            const currentTotal = currentOnline + currentOffline;
            const previousTotal = previousOnline + previousOffline;

            const trendPercentage = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : (currentTotal > 0 ? 100 : null);

            // Debug logging
            if (process.env.NODE_ENV === 'development') {
                console.log('Combined Online/Offline Bookings Trend:', {
                    currentOnline,
                    currentOffline,
                    currentTotal,
                    previousOnline,
                    previousOffline,
                    previousTotal,
                    trendPercentage
                });
            }

            return {
                percentage: trendPercentage,
                currentTotal,
                previousTotal,
                currentPeriod: currentPeriodRange,
                previousPeriod: previousPeriodRange
            };
        };

        // Calculate combined trend for canceled + rejected
        const calculateCombinedTrend = () => {
            if (!dailyAnalytics || dailyAnalytics.length === 0 || !previousDailyAnalytics || previousDailyAnalytics.length === 0) {
                return null;
            }

            let currentCanceled = 0;
            let currentRejected = 0;
            let previousCanceled = 0;
            let previousRejected = 0;

            // Calculate current period totals
            dailyAnalytics.forEach(day => {
                if (spId) {
                    // SP level
                    if (day.SP && day.SP[spId]) {
                        currentCanceled += day.SP[spId]?.counters?.canceled || 0;
                        currentRejected += day.SP[spId]?.counters?.rejected || 0;
                    }
                } else {
                    // General level
                    currentCanceled += day.general?.counters?.canceled || 0;
                    currentRejected += day.general?.counters?.rejected || 0;
                }
            });

            // Calculate previous period totals
            previousDailyAnalytics.forEach(day => {
                if (spId) {
                    // SP level
                    if (day.SP && day.SP[spId]) {
                        previousCanceled += day.SP[spId]?.counters?.canceled || 0;
                        previousRejected += day.SP[spId]?.counters?.rejected || 0;
                    }
                } else {
                    // General level
                    previousCanceled += day.general?.counters?.canceled || 0;
                    previousRejected += day.general?.counters?.rejected || 0;
                }
            });

            const currentTotal = currentCanceled + currentRejected;
            const previousTotal = previousCanceled + previousRejected;

            const trendPercentage = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : (currentTotal > 0 ? 100 : null);

            // Debug logging
            if (process.env.NODE_ENV === 'development') {
                console.log('Combined Canceled/Rejected Trend:', {
                    currentCanceled,
                    currentRejected,
                    currentTotal,
                    previousCanceled,
                    previousRejected,
                    previousTotal,
                    trendPercentage
                });
            }

            return {
                percentage: trendPercentage,
                currentTotal,
                previousTotal,
                currentPeriod: currentPeriodRange,
                previousPeriod: previousPeriodRange
            };
        };

        return {
            bookingsTrend: calculateOnlineOfflineTrend(),
            revenueTrend: calculateTrend('totalRevenu', spId ? 'SP' : 'general'),
            usersTrend: calculateTrend('totalUsers', spId ? 'SP' : 'general'),
            canceledRejectedTrend: calculateCombinedTrend()
        };
    }, [dailyAnalytics, previousDailyAnalytics, spId, currentPeriodRange, previousPeriodRange]);

    // Early return after all hooks
    if (loading) {
        return <div className="loading" />;
    }

    // Debug: Log data to console (remove in production)
    if (process.env.NODE_ENV === 'development') {
        console.log('Daily Analytics Data:', dailyAnalytics);
        // console.log('Bookings Data:', bookingsData);
        console.log('Revenue Data:', revenueData);
    }

    return (
        <>
            {error && (
                <div className="text-center text-muted p-4" style={{ marginBottom: '1rem' }}>
                    Error loading analytics: {error}
                </div>
            )}

            <Colxx xxs="12" lg="6" className="mb-4">
                <InteractiveBarChart
                    title="Daily Bookings"
                    description={dateRangeLabel}
                    data={onlineOfflineBookingsData}
                    dataKeys={[
                        {
                            key: 'online',
                            label: 'Online',
                            color: 'hsl(222.2, 47.4%, 11.2%)'
                        },
                        {
                            key: 'offline',
                            label: 'Offline',
                            color: 'hsl(0, 84.2%, 60.2%)' // Red/Orange
                        }
                    ]}
                    height={300}
                    trendData={bookingsTrend}
                    stacked={true}
                />
            </Colxx>
            <Colxx xxs="12" lg="6" className="mb-4">
                <InteractiveBarChart
                    title="Daily Revenue"
                    description={dateRangeLabel}
                    data={revenueData}
                    dataKeys={[
                        {
                            key: 'totalRevenu',
                            label: 'Total Revenue',
                            color: 'hsl(142.1, 76.2%, 36.3%)'
                        }
                    ]}
                    height={300}
                    trendData={revenueTrend}
                    isCurrency={true}
                />
            </Colxx>
            <Colxx xxs="12" lg="6" className="mb-4">
                <InteractiveBarChart
                    title="Daily Users"
                    description={dateRangeLabel}
                    data={usersData}
                    dataKeys={[
                        {
                            key: 'totalUsers',
                            label: 'Total Users',
                            color: 'hsl(217.2, 91.2%, 59.8%)'
                        }
                    ]}
                    height={300}
                    trendData={usersTrend}
                />
            </Colxx>
            <Colxx xxs="12" lg="6" className="mb-4">
                <InteractiveBarChart
                    title="Daily Canceled & Rejected Orders"
                    description={dateRangeLabel}
                    data={canceledRejectedData}
                    dataKeys={[
                        {
                            key: 'canceled',
                            label: 'Canceled',
                            color: 'hsl(0, 84.2%, 60.2%)' // Darker red
                        },
                        {
                            key: 'rejected',
                            label: 'Rejected',
                            color: 'hsl(0, 70%, 75%)' // Lighter red
                        }
                    ]}
                    height={300}
                    trendData={canceledRejectedTrend}
                    stacked={true}
                    invertTrend={true}
                />
            </Colxx>
        </>
    );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(DashboardCharts, (prevProps, nextProps) => {
    return (
        prevProps.dateRange?.startTimestamp === nextProps.dateRange?.startTimestamp &&
        prevProps.dateRange?.endTimestamp === nextProps.dateRange?.endTimestamp &&
        prevProps.loading === nextProps.loading &&
        prevProps.error === nextProps.error &&
        prevProps.dailyAnalytics === nextProps.dailyAnalytics &&
        prevProps.previousDailyAnalytics === nextProps.previousDailyAnalytics &&
        prevProps.filterBySpId === nextProps.filterBySpId
    );
});
